/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BlockStack,
  Box,
  Button,
  Card,
  IndexFilters,
  IndexFiltersMode,
  IndexTable,
  InlineStack,
  Loading,
  Page,
  Pagination,
  useIndexResourceState,
  useSetIndexFiltersMode,
} from "@medentic/polaris-react";
import type {
  TabProps,
  FilterInterface,
  AppliedFilterInterface,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { debounce } from "lodash";

import { AppPageWrapper } from "@shared/ui";
import { TabsTableSkeleton } from "@shared/ui";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import usePatients from "@features/clinic-patients/hooks/usePatients";
import usePatientsExport from "@features/clinic-patients/hooks/usePatientsExport";
import AddPatientModal from "./AddPatientModal";
import { GroupData } from "../types";
import { useDispatch } from "react-redux";
import { clearFilters, setTab } from "@features/clinic-patients/model/patientSlice";
import { IndexTableHeading } from "@medentic/polaris-react/build/ts/src/components/IndexTable";
import { NonEmptyArray } from "@medentic/polaris-react/build/ts/src/types";
import { ROOTS } from "@app/config/constants";
import {
  getFilterLabel,
  getSortOptions,
  getColumnDisplayOrder,
  getMaxColumnDisplayOrder,
} from "../utils";

import type { Column, Patient, PatientView } from "../types";
import EditColumns from "./EditColumns";
import PatientCellRenderer from "./PatientCellRenderer";
import { FilterComponentRenderer } from "@shared/ui";
import { ImportModal } from "./ImportPatients/ImportModal";
import useTags from "@shared/hooks/common/useTags";

const systemFields = [
  "id",
  "core_index",
  "core_private_note",
  "core_private_note_tone",
];

function PatientList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { clinicId } = useParams<{ clinicId: string }>();

  const { mode, setMode } = useSetIndexFiltersMode();
  const [editedColumns, setEditedColumns] = useState<Column[] | null>(null);
  const [selectedViewIndex, setSelectedViewIndex] = useState(0);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<{ [key: string]: any }>(
    () => {
      const params = new URLSearchParams(window.location.search);
      const initialFilters: { [key: string]: any } = {};
      params.forEach((value, key) => {
        initialFilters[key] = value;
      });
      return initialFilters;
    }
  );
  const [localQuery, setLocalQuery] = useState<string>(
    () => appliedFilters.searchQuery ?? ""
  );
  const { tags } = useTags(clinicId ?? "");
  const tagsById = useMemo(
    () => new Map(tags.map((tag) => [tag.id, tag])),
    [tags]
  );

  const { GET, POST, DELETE, PATCH, PUT } = useApiRequest();
  const { data: schemaData = [], isLoading: isLoadingSchema } = useQuery<
    GroupData[]
  >({
    queryKey: ["patient-schema-for-list", clinicId],
    queryFn: () => GET(`/api/healthcare-companies/${clinicId}/patients/schema`),
    enabled: Boolean(clinicId),
  });

  const {
    data: viewsResponse = [],
    isLoading: isLoadingViews,
    refetch: refetchViews,
  } = useQuery<PatientView[]>({
    queryKey: ["patient-views", clinicId],
    queryFn: async () => {
      return GET(`/api/healthcare-companies/${clinicId}/patients/views`);
      // Suppose it returns an array of PatientView
    },
    enabled: Boolean(clinicId),
  });

  const viewsData = useMemo(() => {
    return [
      { name: t("common.all"), isLocked: true, id: "all" },
      ...viewsResponse,
    ];
  }, [t, viewsResponse]);


  const dynamicSchemaFields: Column[] = useMemo(() => {
    if (!schemaData.length) return [];
    const groups = schemaData[0]?.groups || [];
    const columns: Column[] = [];

    // Get the current view's fields
    const currentView =
      selectedViewIndex > 0 ? viewsData[selectedViewIndex] : null;

    const viewFields = currentView?.filters?.fields || [];

    groups.forEach((g) => {
      g.properties.forEach((prop) => {
        if (!systemFields.includes(prop.name)) {
          columns.push({
            ...prop,
            type: prop.renderer?.type ?? "",
            id: prop.renderer?.id ?? "",
            visible: selectedViewIndex === 0 || viewFields.includes(prop.name),
            displayOrder: getColumnDisplayOrder(
              prop.name,
              viewFields,
              prop.displayOrder ?? 0
            ),
          });
        }
      });
    });

    // add tags column
    if (columns.length > 0 && !columns.some((c) => c.name === "tags")) {
      columns.push({
        name: "tags",
        label: t("patients.columns.tags"),
        visible: selectedViewIndex === 0 ? true : viewFields.includes("tags"),
        type: "tags",
        id: "tags",
        displayOrder: getColumnDisplayOrder(
          "tags",
          viewFields,
          getMaxColumnDisplayOrder(columns) + 1
        ),
      });
    }

    // Sort columns based on displayOrder
    return [...columns].sort((a, b) => {
      // Always put core_avatar first
      if (a.name === "core_avatar") return -1;
      if (b.name === "core_avatar") return 1;
      // Then sort by displayOrder for other columns
      return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
    });
  }, [schemaData, selectedViewIndex, viewsData, t]);

  /** 4) Query/pagination logic with usePatients */
  const {
    data: {
      patients = [],
      paginate = { total: 0, perPage: 50, currentPage: 1, lastPage: 1 },
      filter: responseFilters,
    } = {},
    isFetching: isPatientsLoading,
  } = usePatients(clinicId, {
    appliedFilters,
    columns:
      selectedViewIndex !== 0
        ? dynamicSchemaFields
            ?.filter((c) => !!c.visible)
            .map((prop) => prop.name)
        : [],
  });

  const defaultEditedColumns = useMemo(() => {
    const sortedColumns = [...dynamicSchemaFields].sort((a, b) => {
      // Always put core_avatar first
      if (a.name === "core_avatar") return -1;
      if (b.name === "core_avatar") return 1;
      // Then sort by displayOrder for other columns
      return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
    });
    if (selectedViewIndex === 0) {
      // For "All" view, only show fields that are in responseFilters
      return sortedColumns.map((prop) => ({
        ...prop,
        visible: responseFilters?.fields?.includes(prop.name) ?? false,
      }));
    } else {
      // For other views, use dynamicSchemaFields as is
      return sortedColumns;
    }
  }, [selectedViewIndex, dynamicSchemaFields, responseFilters?.fields]);
  const activeEditedColumns = editedColumns ?? defaultEditedColumns;

  const visibleColumns = useMemo(() => {
    return dynamicSchemaFields.filter(
      (prop) => prop.visible && responseFilters?.fields.includes(prop.name)
    );
  }, [dynamicSchemaFields, responseFilters]);

  const tableHeadings: NonEmptyArray<IndexTableHeading> = useMemo(() => {
    const columns = activeEditedColumns.filter((c) => c.visible);
    if (columns.length === 0) {
      return [{ title: "" }];
    }
    return columns.map((prop) => ({
      title: prop.name === "core_avatar" ? "" : prop.label,
    })) as NonEmptyArray<IndexTableHeading>;
  }, [activeEditedColumns]);

  const createViewMutation = useMutation({
    mutationFn: async (name: string) => {
      return await POST<PatientView>(
        `/api/healthcare-companies/${clinicId}/patients/views`,
        {
          name,
          filters: {
            ...appliedFilters,
            fields: activeEditedColumns
              .filter((c) => c.visible)
              .map((col) => col.name),
          },
        }
      );
    }
  });

  /** Save (edit existing view) => PATCH /patients/views/{id} */
  const renameViewMutation = useMutation({
    mutationFn: async ({ id, newData }: { id: string; newData: any }) => {
      return PATCH(
        `/api/healthcare-companies/${clinicId}/patients/views/${id}`,
        { ...newData }
      );
    },
    onSuccess: () => {
      refetchViews();
    },
  });

  /** Save (edit existing view) => PATCH /patients/views/{id} */
  const updateViewMutation = useMutation({
    mutationFn: async ({ id, newData }: { id: string; newData: any }) => {
      return PUT(`/api/healthcare-companies/${clinicId}/patients/views/${id}`, {
        ...newData,
      });
    },
    onSuccess: () => {
      refetchViews();
    },
  });

  /** Duplicate => POST a new view with same config but different name */
  const duplicateViewMutation = useMutation({
    mutationFn: async ({ name, filters }: { name: string; filters: any }) => {
      return await POST<PatientView>(
        `/api/healthcare-companies/${clinicId}/patients/views`,
        {
          name,
          filters: { ...filters },
        }
      );
    }
  });

  /** Delete => DELETE /patients/views/{id} */
  const deleteViewMutation = useMutation({
    mutationFn: async (id: string) => {
      return DELETE(
        `/api/healthcare-companies/${clinicId}/patients/views/${id}`
      );
    },
    onSuccess: () => {
      refetchViews();
    },
  });

  const handleRenameView = async (index: number, newName: string) => {
    const item = viewsData[index];
    if (!item) return false;
    await renameViewMutation.mutateAsync({
      id: item.id,
      newData: { name: newName },
    });
    return true;
  };

  const handleDuplicateView = async (
    _originalIndex: number,
    newName: string
  ) => {
    // Could read the original's columns/filters
    // For demonstration, we pass just { name: newName }
    const dataToDuplicate = viewsData[_originalIndex].filters;
    const createdView = await duplicateViewMutation.mutateAsync({
      name: newName,
      filters: dataToDuplicate,
    });
    const refreshedViewsResult = await refetchViews();
    const refreshedViews = refreshedViewsResult.data ?? [];
    const duplicatedViewIndex = refreshedViews.findIndex(
      (view) => view.id === createdView.id
    );
    if (duplicatedViewIndex > -1) {
      const nextView = refreshedViews[duplicatedViewIndex];
      setSelectedViewIndex(duplicatedViewIndex + 1);
      setEditedColumns(null);
      if (nextView.filters?.tab) {
        dispatch(setTab(nextView.filters.tab));
      }
      if (nextView.filters) {
        setAppliedFilters(nextView.filters);
        setLocalQuery(nextView.filters.searchQuery ?? "");
      }
    }
    return true;
  };

  const handleDeleteView = async (index: number) => {
    const item = viewsData[index];
    if (!item) return false;
    await deleteViewMutation.mutateAsync(item.id);
    setSelectedViewIndex(0);
    setEditedColumns(null);
    setLocalQuery("");
    setAppliedFilters({});
    dispatch(clearFilters());
    updateUrlWithFilters({});
    // after success => refetchViews
    return true;
  };

  // Create new or "Save as"
  const handleCreateNewView = async (value: string) => {
    const createdView = await createViewMutation.mutateAsync(value);
    const refreshedViewsResult = await refetchViews();
    const refreshedViews = refreshedViewsResult.data ?? [];
    const createdViewIndex = refreshedViews.findIndex(
      (view) => view.id === createdView.id
    );
    if (createdViewIndex > -1) {
      const nextView = refreshedViews[createdViewIndex];
      setSelectedViewIndex(createdViewIndex + 1);
      setEditedColumns(null);
      if (nextView.filters?.tab) {
        dispatch(setTab(nextView.filters.tab));
      }
      if (nextView.filters) {
        setAppliedFilters(nextView.filters);
        setLocalQuery(nextView.filters.searchQuery ?? "");
      }
    }
    return true;
  };

  // Save changes to existing => e.g. reorder columns
  const handleSaveExistingView = async () => {
    const item = viewsData[selectedViewIndex];
    if (!item) return false;
    // e.g. call updateViewMutation with new columns
    // For demonstration:
    await updateViewMutation.mutateAsync({
      id: item.id,
      newData: {
        name: item.name,
        filters: {
          ...appliedFilters,
          fields: activeEditedColumns
            .filter((c) => c.visible)
            .map((col) => col.name),
        },
      },
    });
    return true;
  };

  // Build polaris-style TabProps
  const tabs: TabProps[] = viewsData.map((view, index) => ({
    content: view.name,
    index,
    id: view.id,
    isLocked: view.isLocked,
    // onAction can be a function if needed
    onAction: () => {
      // maybe do something when user clicks the tab action
    },
    actions: view.isLocked
      ? []
      : [
          {
            type: "rename",
            onAction: () => {},
            onPrimaryAction: async (newVal: string): Promise<boolean> => {
              await handleRenameView(index, newVal);
              return true;
            },
          },
          {
            type: "duplicate",
            onPrimaryAction: async (newVal: string): Promise<boolean> => {
              await handleDuplicateView(index, newVal);
              return true;
            },
          },
          {
            type: "delete",
            onPrimaryAction: async () => {
              await handleDeleteView(index);
              return true;
            },
          },
        ],
  }));

  const [sortSelected, setSortSelected] = useState(["core_index desc"]);

  const primaryAction =
    selectedViewIndex === 0
      ? {
          type: "save-as" as "save-as" | "save" | "cancel",
          onAction: handleCreateNewView,
          disabled: false,
          loading: createViewMutation.isPending,
        }
      : {
          type: "save" as "save-as" | "save" | "cancel",
          onAction: handleSaveExistingView,
          disabled: false,
          loading: updateViewMutation.isPending,
        };

  const { refetch: exportPatients, isFetching: isExporting } =
    usePatientsExport(clinicId, {
      appliedFilters,
      columns:
        selectedViewIndex !== 0 ? visibleColumns.map((prop) => prop.name) : [],
    });

  const { total, perPage, currentPage, lastPage } = paginate;
  const handleNextPage = () => {
    if (currentPage < lastPage) {
      handleFiltersChange("page", currentPage + 1);
    }
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handleFiltersChange("page", currentPage - 1);
    }
  };

  /** rowMarkup as before */
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(
      patients.map((p) => ({
        id: p.id,
      }))
    );

  const rowMarkup = useMemo(() => {
    return patients.map((p, index) => (
      <IndexTable.Row
        id={p.id}
        key={p.id}
        position={index}
        selected={selectedResources.includes(p.id)}
        onClick={() => {
          navigate(`${ROOTS.CLINIC}/${clinicId}/patients/${p.id}`);
        }}
      >
        {visibleColumns.map((column) => (
          <IndexTable.Cell key={column.name}>
            <PatientCellRenderer
              column={column}
              patient={p as Patient}
              tagsById={tagsById}
            />
          </IndexTable.Cell>
        ))}
      </IndexTable.Row>
    ));
  }, [
    patients,
    selectedResources,
    visibleColumns,
    clinicId,
    navigate,
    tagsById,
  ]);

  const updateUrlWithFilters = useCallback(
    (filters: { [key: string]: any }) => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") {
          params.append(k, String(v));
        }
      });
      navigate(`?${params.toString()}`, { replace: true });
    },
    [navigate]
  );

  const handleFiltersChange = useCallback(
    (key: string, value: any) => {
      setAppliedFilters((prevFilters) => {
        const newFilters = {
          ...prevFilters,
          [key]:
            key === "sort"
              ? `${(value[0] || "").split(" ")[0]} ${
                  (value[0] || "").split(" ")[1]
                }`
              : value,
        };
        const params = new URLSearchParams();
        Object.entries(newFilters).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "") {
            params.append(k, String(v));
          }
        });
        navigate(`?${params.toString()}`, { replace: true });
        return newFilters;
      });
    },
    [navigate]
  );

  const dynamicFilters = useMemo(() => {
    if (!schemaData.length) return [];
    const groups = schemaData[0]?.groups || [];
    const filterableProperties = schemaData[0]?.filterableProperties || [];
    const filters: FilterInterface[] = [];

    groups.forEach((g) => {
      g.properties
        .filter((prop) => filterableProperties.includes(prop.name))
        .forEach((prop: any) => {
          // Create base filter object
          const baseFilter: FilterInterface = {
            key: prop.name,
            label: prop.label,
            filter: (
              <FilterComponentRenderer
                key={prop.name}
                id={prop.renderer?.id ?? "textInput"}
                label={prop.label}
                value={appliedFilters[prop.name]}
                onChange={(val) => {
                  if (val !== appliedFilters[prop.name]) {
                    handleFiltersChange(prop.name, val);
                  } else if (document.activeElement instanceof HTMLElement) {
                    document.body.click();
                  }
                }}
                disabled={false}
                helpText={prop.description}
                placeholder=""
                comboOptions={prop.options ?? []}
              />
            ),
          };

          filters.push(baseFilter);
        });
    });

    filters.push({
      key: "tags",
      label: t("tags.tags"),
      filter: (
        <FilterComponentRenderer
          key="tags"
          id="combobox"
          label={t("tags.tags")}
          value={appliedFilters["tags"]}
          onChange={(val) => {
            if (val !== appliedFilters["tags"]) {
              handleFiltersChange("tags", val);
            } else if (document.activeElement instanceof HTMLElement) {
              document.body.click();
            }
          }}
          disabled={false}
          helpText=""
          placeholder=""
          comboOptions={tags.map((tag) => ({
            label: tag.name,
            value: tag.id,
          }))}
        />
      ),
    });

    // Sort filters alphabetically by label
    return filters.sort((a, b) => a.label.localeCompare(b.label));
  }, [schemaData, appliedFilters, handleFiltersChange, tags, t]);

  const handleFiltersClearAll = useCallback(() => {
    setAppliedFilters({});
    setLocalQuery("");
    dispatch(clearFilters());
    updateUrlWithFilters({});
  }, [dispatch, updateUrlWithFilters]);

  /** Query input */
  const debouncedSetQuery = useMemo(
    () =>
      debounce((val: string) => {
        handleFiltersChange("searchQuery", val);
      }, 1000),
    [handleFiltersChange]
  );
  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);
  const handleQueryChange = useCallback(
    (val: string) => {
      setLocalQuery(val);
      debouncedSetQuery(val);
    },
    [debouncedSetQuery]
  );

  const handleQueryClear = useCallback(() => {
    setLocalQuery("");
    debouncedSetQuery("");
  }, [debouncedSetQuery]);

  const onHandleCancel = () => {
    setEditedColumns(null);
    handleFiltersClearAll();
  };

  const appliedFiltersArray: AppliedFilterInterface[] = Object.entries(
    appliedFilters
  ).map(([key, value]) => {
    const filterValue = getFilterLabel(
      key,
      value,
      dynamicSchemaFields,
      key === "tags"
        ? tags.map((tag) => ({
            label: tag.name,
            value: tag.id,
          }))
        : undefined
    );
    return {
      key,
      label: `${
        dynamicFilters.find((f) => f.key === key)?.label ?? key
      }: ${filterValue}`,
      onRemove: () => {
        const newFilters = { ...appliedFilters };
        delete newFilters[key];
        setAppliedFilters(newFilters);
        updateUrlWithFilters(newFilters);
        if (key === "searchQuery") {
          setLocalQuery("");
        }
      },
    };
  });

  const handleExport = async () => {
    const dataUrl = await exportPatients();
    // Create an anchor element
    if (!dataUrl.data?.url) return;
    const link = document.createElement("a");
    link.href = dataUrl.data?.url;
    link.download = "patients-list.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [showImport, setShowImport] = useState(
    new URLSearchParams(window.location.search).get("openImportModal") ===
      "true"
  );

  // Create memoized sort options
  const sortOptionsData = useMemo(() => {
    return getSortOptions(t, schemaData);
  }, [t, schemaData]);

  const handleTabChange = (index: number) => {
    setSelectedViewIndex(index);
    setEditedColumns(null);
    if (index === 0) {
      handleFiltersClearAll();
      return;
    }

    const selectedView = viewsData[index];
    if (selectedView?.filters?.tab) {
      dispatch(setTab(selectedView.filters.tab));
    }
    if (selectedView?.filters) {
      setAppliedFilters(selectedView.filters);
      setLocalQuery(selectedView.filters.searchQuery ?? "");
    }
  };

  return (
    <AppPageWrapper>
      <Page
        fullWidth
        title={t("patients.title")}
        primaryAction={
          <Button onClick={() => setShowAddPatient(true)}>
            {t("patients.addPatient")}
          </Button>
        }
        secondaryActions={[
          {
            content: t("patients.importCTA"),
            onAction: () => setShowImport(true),
          },
          {
            content: t("patients.export"),
            onAction: () => handleExport(),
            disabled: patients.length === 0,
            loading: isExporting,
          },
        ]}
      >
        {isLoadingViews || isLoadingSchema ? (
          <TabsTableSkeleton />
        ) : (
          <BlockStack align="center">
            <Card padding="0">
              <IndexFilters
                loading={
                  createViewMutation.isPending ||
                  updateViewMutation.isPending ||
                  isPatientsLoading
                }
                sortOptions={sortOptionsData}
                sortSelected={sortSelected}
                onSort={(val) => {
                  setSortSelected(val);
                  handleFiltersChange("sort", val);
                }}
                queryValue={localQuery}
                queryPlaceholder={
                  selectedViewIndex === 0
                    ? t("patients.searchPlaceholderAll")
                    : t("patients.searchPlaceholderView", {
                        view: viewsData[selectedViewIndex].name,
                      })
                }
                onQueryChange={handleQueryChange}
                onQueryClear={handleQueryClear}
                primaryAction={primaryAction}
                cancelAction={{
                  onAction: onHandleCancel,
                  disabled: false,
                  loading: false,
                }}
                tabs={tabs}
                selected={selectedViewIndex}
                onSelect={handleTabChange}
                canCreateNewView
                onCreateNewView={handleCreateNewView}
                filters={dynamicFilters}
                appliedFilters={appliedFiltersArray}
                onClearAll={handleFiltersClearAll}
                mode={mode}
                setMode={setMode}
                showEditColumnsButton
              />
              {mode === IndexFiltersMode.EditingColumns ? (
                <EditColumns
                  data={patients}
                  onColumnsChange={setEditedColumns}
                  columns={activeEditedColumns}
                  tagsById={tagsById}
                />
              ) : (
                <IndexTable
                  resourceName={{
                    singular: t("patients.patient"),
                    plural: t("patients.title"),
                  }}
                  itemCount={visibleColumns.length > 0 ? patients.length : 0}
                  selectedItemsCount={
                    allResourcesSelected ? "All" : selectedResources.length
                  }
                  onSelectionChange={handleSelectionChange}
                  headings={tableHeadings}
                >
                  {rowMarkup}
                </IndexTable>
              )}
            </Card>
            {/* Pagination */}
            <Box paddingBlockEnd="300" />
            <InlineStack align="center">
              <Pagination
                onPrevious={handlePreviousPage}
                onNext={handleNextPage}
                hasPrevious={currentPage > 1}
                hasNext={currentPage < lastPage}
                label={`${Math.min(
                  (currentPage - 1) * perPage + 1,
                  total
                )}-${Math.min(currentPage * perPage, total)} of ${total}`}
              />
            </InlineStack>
          </BlockStack>
        )}
      </Page>

      {(isPatientsLoading || isLoadingViews || isLoadingSchema) && <Loading />}

      {showAddPatient && (
        <AddPatientModal
          open={showAddPatient}
          onClose={() => setShowAddPatient(false)}
        />
      )}

      {showImport && (
        <ImportModal
          open={showImport}
          onClose={() => setShowImport(false)}
          onImportSuccess={() => {
            setShowImport(false);
          }}
        />
      )}
    </AppPageWrapper>
  );
}

export default PatientList;
